/**
 * Offer notification templates (TO-140): a tenant's library of reusable Liquid
 * messages for offer notifications, the offer's chosen message, and the message
 * `offer.sent` carries to the notifier.
 */

/**
 * The channel a template is written for: the shipped `OfferChannel` values plus
 * `ALL` (any channel). A separate enum so `ALL` can never become a send channel.
 * Declaration order is the library's list order.
 */
export enum OfferNotificationTemplateChannel {
	EMAIL = 'EMAIL',
	SMS = 'SMS',
	WHATSAPP = 'WHATSAPP',
	ALL = 'ALL',
}

/** Where a template came from: the TODAY seeder (`CORE`) or a tenant admin (`TENANT`). Never changes. */
export enum NotificationTemplateSource {
	CORE = 'CORE',
	TENANT = 'TENANT',
}

/** Why an offer's custom notification body was refused. */
export enum OfferNotificationBodyInvalidReason {
	LENGTH = 'LENGTH',
	SYNTAX = 'SYNTAX',
}

/** A template as the HTTP surface returns it. */
export interface NotificationTemplateDTO {
	id: string
	name: string
	description: string | null
	channelType: OfferNotificationTemplateChannel
	/** Lowercase primary language subtag (`es`, `gl`, `ca`, `eu`, …). */
	locale: string
	/** Non-null exactly for `EMAIL` templates. */
	subject: string | null
	body: string
	isDefaultForChannel: boolean
	isActive: boolean
	source: NotificationTemplateSource
	/** Null for seeded templates. */
	createdBy: string | null
	createdAt: string
	updatedAt: string
}

/**
 * The values a message template can read (TO-140 offer-notification-dispatch
 * REQ-202), without `offer.short_url` — the consumer completes it with the link.
 */
export interface OfferMessageContext {
	customer: { full_name: string }
	offer: { product_names: string[]; expires_at: string | null; locale: string }
	manager: { full_name: string; branch_name: string }
}

/** The context with the absolute customer link added. */
export interface OfferMessageRenderContext {
	customer: OfferMessageContext['customer']
	offer: OfferMessageContext['offer'] & { short_url: string }
	manager: OfferMessageContext['manager']
}

/** The message `offer.sent` carries: the Liquid source and the frozen context (TO-140). */
export interface OfferSentMessage {
	/** The linked template, or null for a custom body. */
	templateId: string | null
	/** The template's subject (EMAIL templates only); null otherwise. */
	subject: string | null
	body: string
	context: OfferMessageContext
}
